<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Alert' });
export type AlertSeverity = 'info' | 'success' | 'warning' | 'warn' | 'error' | 'danger' | 'secondary';

const props = withDefaults(
	defineProps<{
		severity?: AlertSeverity | string;
		title?: string;
		icon?: string;
		loading?: boolean;
		closable?: boolean;
		dismissLabel?: string;
	}>(),
	{ severity: 'info', title: '', icon: '', loading: false, closable: false, dismissLabel: 'Dismiss notification' }
);
const emit = defineEmits<{ close: [] }>();
const attrs = useAttrs();
const visible = ref(true);
const normalizedSeverity = computed(() => (props.severity === 'warn' ? 'warning' : props.severity === 'danger' ? 'error' : props.severity));
const severityClasses = computed(() => {
	const classes: Record<string, string> = {
		info: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-100',
		success: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100',
		warning: 'border-amber-300 bg-amber-100 text-amber-950 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100',
		error: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200',
		secondary: 'border-surface bg-[var(--egs-content-hover)] text-color'
	};
	return classes[normalizedSeverity.value] ?? classes['info']!;
});
const resolvedIcon = computed(() => {
	if (props.loading) return 'circle-notch';
	if (props.icon) return props.icon;
	const icons: Record<string, string> = { info: 'circle-info', success: 'circle-check', warning: 'triangle-exclamation', error: 'circle-exclamation', secondary: 'circle-info' };
	return icons[normalizedSeverity.value] ?? icons['info']!;
});
const role = computed<'alert' | 'status'>(() => (normalizedSeverity.value === 'warning' || normalizedSeverity.value === 'error' ? 'alert' : 'status'));

function close() {
	visible.value = false;
	emit('close');
}
</script>

<template>
	<div v-if="visible" v-bind="attrs" class="relative flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3" :class="[severityClasses, closable ? 'min-h-12 pr-14' : '', attrs['class']]" :role="role" :aria-busy="loading ? 'true' : 'false'">
		<div class="flex min-w-0 items-start gap-3">
			<slot name="icon" :icon="resolvedIcon"><font-awesome-icon :icon="resolvedIcon" :spin="loading" class="mt-0.5 shrink-0" /></slot>
			<div class="min-w-0">
				<div v-if="title || $slots['title']" class="text-sm font-semibold">
					<slot name="title">{{ title }}</slot>
				</div>
				<div v-if="$slots['default']" :class="title || $slots['title'] ? 'mt-0.5 text-xs opacity-80' : 'text-sm'"><slot /></div>
			</div>
		</div>
		<div v-if="$slots['actions']" class="flex shrink-0 items-center gap-2">
			<slot name="actions" />
		</div>
		<button
			v-if="closable"
			type="button"
			class="absolute right-2 top-2 inline-flex size-8 items-center justify-center rounded-full transition hover:bg-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 dark:hover:bg-white/10"
			:aria-label="dismissLabel"
			@click="close"
		>
			<font-awesome-icon icon="xmark" />
		</button>
	</div>
</template>
